import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { User, Mail, Lock, Phone, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PortalSwitcher from "@/components/PortalSwitcher";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CustomerAuthPage({ mode = "login" }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });

  // Pre-fill email if passed via ?email= (from TrackBookingsNudge on homepage)
  useEffect(() => {
    const emailParam = searchParams.get("email");
    if (emailParam) {
      setForm((f) => ({ ...f, email: emailParam }));
    }
  }, [searchParams]);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const path = mode === "register" ? "/api/customer/register" : "/api/customer/login";
    const url = `${API}${path}`;
    try {
      // Use axios (XHR-based) instead of fetch + res.json().
      //
      // Why: when CapacitorHttp is enabled, native iOS routes fetch() through
      // URLSession and the returned Response object has its body stream
      // already consumed/locked, so calling `await res.json()` throws
      //   TypeError: Body is disturbed or locked
      // axios uses XMLHttpRequest under the hood, which CapacitorHttp handles
      // correctly (proven by the working item-types call in HomePage.jsx).
      const response = await axios.post(url, form, {
        headers: { "Content-Type": "application/json" },
        // Don't throw on 4xx/5xx — we want to read the body for the error detail
        validateStatus: () => true,
      });
      if (response.status < 200 || response.status >= 300) {
        toast.error(
          response.data?.detail ||
            (mode === "register" ? "Sign up failed" : "Login failed")
        );
        return;
      }
      localStorage.setItem("customer_token", response.data.access_token);
      localStorage.setItem("customer_profile", JSON.stringify(response.data.customer));
      toast.success(mode === "register" ? "Welcome aboard!" : "Welcome back");
      navigate("/customer/dashboard");
    } catch (err) {
      toast.error(`Network error: ${err?.message || "unknown"}`);
    } finally {
      setLoading(false);
    }
  };

  const isRegister = mode === "register";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-orange-900 flex flex-col">
      <PortalSwitcher />
      <div className="flex-1 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center mb-6">
          <h1 className="text-2xl font-bold text-white">Transport Anything 247</h1>
          <p className="text-xs text-orange-200">Customer Portal</p>
        </Link>

        <div className="bg-white rounded-2xl shadow-2xl p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-1" data-testid="customer-auth-title">
            {isRegister ? "Create your account" : "Welcome back"}
          </h2>
          <p className="text-sm text-slate-500 mb-6">
            {isRegister ? "Sign up to track and manage your bookings" : "Sign in to your customer account"}
          </p>

          <form onSubmit={submit} className="space-y-3">
            {isRegister && (
              <div className="relative">
                <User className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <Input
                  required
                  placeholder="Full name"
                  value={form.name}
                  onChange={setField("name")}
                  className="pl-10 h-11"
                  data-testid="customer-name-input"
                />
              </div>
            )}
            <div className="relative">
              <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
              <Input
                required
                type="email"
                placeholder="Email address"
                value={form.email}
                onChange={setField("email")}
                className="pl-10 h-11"
                data-testid="customer-email-input"
              />
            </div>
            {isRegister && (
              <div className="relative">
                <Phone className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <Input
                  required
                  placeholder="Phone number"
                  value={form.phone}
                  onChange={setField("phone")}
                  className="pl-10 h-11"
                  data-testid="customer-phone-input"
                />
              </div>
            )}
            <div className="relative">
              <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
              <Input
                required
                type="password"
                placeholder={isRegister ? "Create a password (min 6 chars)" : "Password"}
                value={form.password}
                onChange={setField("password")}
                className="pl-10 h-11"
                data-testid="customer-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
              data-testid="customer-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  {isRegister ? "Create account" : "Sign in"} <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-slate-100 text-center text-sm text-slate-500">
            {isRegister ? (
              <>Already registered? <Link to="/customer/login" className="text-orange-600 font-medium">Sign in</Link></>
            ) : (
              <>New here? <Link to="/customer/register" className="text-orange-600 font-medium">Create an account</Link></>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-orange-200 mt-4">
          Don&rsquo;t have a booking yet? <Link to="/" className="underline">Get an instant quote</Link>
        </p>
      </div>
      </div>
    </div>
  );
}
